import { Extension } from '@tiptap/core'
import { TextSelection } from 'prosemirror-state'
import { Fragment } from 'prosemirror-model'

export const MergeCodeBlock = Extension.create({
  name: 'mergeCodeBlock',

  addCommands() {
    return {
      mergeCodeBlock:
        () =>
        ({ state, dispatch }) => {
          const { schema, tr, selection, doc } = state
          const { codeBlock, paragraph } = schema.nodes
          const { from, to } = selection

          const $from = selection.$from
          const $to = selection.$to

          // 1. Check if selection is inside a single code block node
          if ($from.sameParent($to) && $from.parent.type === codeBlock) {
            const codeNode = $from.parent
            const parentPos = $from.before() // position of codeBlock node in doc

            const startOffset = $from.parentOffset
            const endOffset = $to.parentOffset

            // Text before selection
            const beforeText = codeNode.textBetween(0, startOffset, '\n')
            // Selected text
            const selectedText = codeNode.textBetween(startOffset, endOffset, '\n')
            // Text after selection
            const afterText = codeNode.textBetween(endOffset, codeNode.content.size, '\n')

            const nodes = []

            // If text before selection exists, keep it inside code block
            if (beforeText.length > 0) {
              nodes.push(codeBlock.create(null, schema.text(beforeText)))
            }

            // Selected text goes inside paragraph
            if (selectedText.length > 0) {
              nodes.push(paragraph.create(null, schema.text(selectedText)))
            }

            // Text after selection back to code block
            if (afterText.length > 0) {
              nodes.push(codeBlock.create(null, schema.text(afterText)))
            }

            // Replace the entire original code block with new nodes
            tr.replaceWith(parentPos, parentPos + codeNode.nodeSize, Fragment.fromArray(nodes))

            // After replaceWith, get the updated document node at parentPos
            const newNode = tr.doc.nodeAt(parentPos)

            // Find the paragraph node position inside the replaced fragment
            // The paragraph node is at index = (beforeText.length > 0 ? 1 : 0)
            const paraIndex = beforeText.length > 0 ? 1 : 0

            if (!newNode || !newNode.childCount) {
                // fallback: set selection at parentPos + 1 safely
                tr.setSelection(TextSelection.create(tr.doc, Math.min(parentPos + 1, tr.doc.content.size)))
                } else {
                // Calculate paragraph node position inside newNode
                let posInside = 0
                for (let i = 0; i < paraIndex; i++) {
                    posInside += newNode.child(i).nodeSize
                }
                // position of paragraph node start in document:
                const paraPos = parentPos + 1 + posInside
                tr.setSelection(TextSelection.create(tr.doc, paraPos))
            }

            tr.scrollIntoView()

            if (dispatch) dispatch(tr)
            return true
          }

          // 2. Check if selection fully covers code block(s) (existing logic)
          let isFullyInCodeBlock = true
          doc.nodesBetween(from, to, (node, pos) => {
            if (node.isBlock && node.type !== codeBlock) {
              isFullyInCodeBlock = false
              return false
            }
            return true
          })

          if (isFullyInCodeBlock) {
            // Replace all selected code blocks with paragraphs
            let replaced = false
            doc.nodesBetween(from, to, (node, pos) => {
              if (node.type === codeBlock) {
                const para = paragraph.create(null, node.content)
                tr.replaceWith(pos, pos + node.nodeSize, para)
                replaced = true
                return false
              }
              return true
            })

            if (replaced && dispatch) {
              tr.setSelection(TextSelection.create(tr.doc, Math.min(from, tr.doc.content.size))).scrollIntoView()
              dispatch(tr)
              return true
            }

            return false
          }

          // 3. Otherwise wrap selection in a code block (existing logic)
          const selectedText = doc.textBetween(from, to, '\n')
          const text = selectedText.trim() ? selectedText : ' '
          const codeBlockNode = codeBlock.create(null, schema.text(text))

          tr.replaceRangeWith(from, to, codeBlockNode)
          tr.setSelection(TextSelection.create(tr.doc, from + 1))
          tr.scrollIntoView()

          if (dispatch) dispatch(tr)
          return true
        },
    }
  },
})